import {useMemo} from 'react';
import {useGetLocaleQuery} from '@/services/api/locale-api';
import {useSelectedZone} from '@/services/geoblocking/hooks/useSelectedZone';
import {formatStorePrice} from '@/utils/ecommerce/currency';

/**
 * useFormatPrice - React hook for formatting price values.
 *
 * Basic usage (locale/currency resolved from API):
 *   const formatted = useFormatPrice(price);
 *
 * With explicit locale/currency (skips API lookup):
 *   const formatted = useFormatPrice(price, { formattedLocale, currency });
 *
 * With Intl options and whole-number decimal omission:
 *   const formatted = useFormatPrice(price, { formattedLocale, currency, numberFormatOptions: { minimumFractionDigits: 2 }, omitDecimalsIfWhole: true });
 */

/**
 * Formats a price value according to locale and currency.
 * @param {number|string} price - The price value to format.
 * @param {Object} [options] - Optional overrides.
 * @param {Object} [options.numberFormatOptions] - Intl.NumberFormat options (e.g. minimumFractionDigits, maximumFractionDigits).
 * @param {number} [options.numberFormatOptions.minimumFractionDigits] - Minimum fraction digits.
 * @param {number} [options.numberFormatOptions.maximumFractionDigits] - Maximum fraction digits.
 * @param {string} [options.formattedLocale] - Explicit locale (e.g. "en-US"). Skips API lookup when provided with currency.
 * @param {Object} [options.currency] - Explicit currency object. Skips API lookup when provided with formattedLocale.
 * @param {string} [options.currency.currencyPlacement] - Position of the currency symbol relative to the value (e.g. "before", "after").
 * @param {string} [options.currency.currencySymbol] - Currency symbol to display (e.g. "$", "€", "USD").
 * @param {boolean} [options.omitDecimalsIfWhole=false] - When true, omits decimal places for whole-number prices.
 * @returns {string|null} Formatted price string, or null if formatting fails.
 */
export const useFormatPrice = (price, {numberFormatOptions, formattedLocale: explicitFormattedLocale, currency: explicitCurrency, omitDecimalsIfWhole = false} = {}) => {
  // Stabilize object params using JSON serialization for deep equality to avoid referential churn from inline objects
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const stableOptions = useMemo(() => numberFormatOptions, [JSON.stringify(numberFormatOptions)]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const stableCurrency = useMemo(() => explicitCurrency, [JSON.stringify(explicitCurrency)]);

  const hasExplicitParams = !!explicitFormattedLocale && !!stableCurrency;
  const zone = useSelectedZone();
  const {data: locale, isSuccess} = useGetLocaleQuery({zone}, {skip: !zone || hasExplicitParams});

  return useMemo(() => {
    if (price == null || Number.isNaN(Number(price))) return null;

    const formattedLocale = explicitFormattedLocale ?? locale?.supportedLocales?.[0]?.formattedLocale;
    const currency = stableCurrency ?? locale?.supportedCurrencies?.[0];

    if (!formattedLocale || !currency) return null;
    if (!hasExplicitParams && !isSuccess) return null;

    const resolvedOptions = omitDecimalsIfWhole && price % 1 === 0
      ? {...stableOptions, maximumFractionDigits: 0}
      : stableOptions;

    return formatStorePrice(price, formattedLocale, currency, resolvedOptions);
  }, [isSuccess, locale, price, stableOptions, explicitFormattedLocale, stableCurrency, hasExplicitParams, omitDecimalsIfWhole]);
};
